import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Loader2, LogOut, Search, Download, Mail, Phone, Building2, ArrowUpRight, Inbox,
} from "lucide-react";
import { LOGO_DARK } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "fbf_admin_token";

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

const roleLabel = { architect: "Architect", builder: "Builder", "interior-designer": "Interior Designer", homeowner: "Homeowner", other: "Other" };

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      toast.success("Welcome back.");
      onSuccess();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-bone">
      <div className="hidden lg:flex flex-col justify-between bg-royal text-white p-14 grain relative overflow-hidden">
        <img src={LOGO_DARK} alt="logo" className="h-14 w-14 object-contain rounded-md bg-white p-1.5 relative z-10" />
        <div className="relative z-10">
          <h1 className="font-display text-5xl leading-[0.95] tracking-tight">
            Leads <span className="italic text-white/70">Dashboard.</span>
          </h1>
          <p className="mt-5 text-white/70 max-w-sm">
            Every quote request from architects and builders, in one private place.
          </p>
        </div>
        <span className="relative z-10 text-xs tracking-[0.24em] uppercase text-white/50">Beyond Floors. Beyond Expectations.</span>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="admin-login-form">
          <span className="text-xs tracking-[0.28em] uppercase font-semibold text-royal">Admin access</span>
          <h2 className="mt-4 font-display text-4xl tracking-tight text-royal-deep">Sign in</h2>
          <div className="mt-10 space-y-7">
            <input
              data-testid="login-email"
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-transparent border-b-2 border-black/10 focus:border-royal outline-none py-3 text-royal-deep transition-colors duration-300"
            />
            <input
              data-testid="login-password"
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent border-b-2 border-black/10 focus:border-royal outline-none py-3 text-royal-deep transition-colors duration-300"
            />
          </div>
          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="mt-10 w-full inline-flex items-center justify-center gap-2 bg-royal text-white text-sm font-medium py-3.5 rounded-full hover:bg-royal-dark transition-colors duration-300 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {loading ? "Signing in..." : "Sign in"}
            {!loading && <ArrowUpRight className="h-4 w-4" />}
          </button>
          <a href="/" className="mt-6 block text-center text-sm text-muted-foreground hover:text-royal transition-colors">← Back to website</a>
        </form>
      </div>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [leads, setLeads] = useState(null);
  const [q, setQ] = useState("");
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/leads`, { headers: authHeader() });
      setLeads(data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Failed to load leads.");
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!leads) return [];
    const s = q.trim().toLowerCase();
    if (!s) return leads;
    return leads.filter((l) =>
      [l.name, l.email, l.company, l.role, l.interest, l.message]
        .filter(Boolean).some((v) => v.toLowerCase().includes(s))
    );
  }, [leads, q]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/leads/export`, { headers: authHeader(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "fascinated-by-floors-leads.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded.");
    } catch {
      toast.error("Export failed.");
    } finally {
      setExporting(false);
    }
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-bone">
      <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-xl border-b border-black/5">
        <div className="max-w-[1200px] mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <img src={LOGO_DARK} alt="logo" className="h-9 w-9 object-contain" />
            <span className="font-display text-lg text-royal-deep">Leads Dashboard</span>
          </a>
          <button
            data-testid="admin-logout"
            onClick={onLogout}
            className="inline-flex items-center gap-2 text-sm text-royal-deep/70 hover:text-royal transition-colors"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 md:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-8">
          <div>
            <h1 className="font-display text-4xl tracking-tight text-royal-deep">Quote requests</h1>
            <p className="mt-2 text-muted-foreground" data-testid="leads-count">
              {leads === null ? "Loading…" : `${leads.length} total lead${leads.length === 1 ? "" : "s"}`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                data-testid="leads-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, email, company…"
                className="w-64 max-w-full bg-white border border-black/10 rounded-full pl-9 pr-4 py-2.5 text-sm outline-none focus:border-royal transition-colors"
              />
            </div>
            <button
              data-testid="export-csv"
              onClick={exportCsv}
              disabled={exporting || !leads?.length}
              className="inline-flex items-center gap-2 bg-royal text-white text-sm font-medium px-4 py-2.5 rounded-full hover:bg-royal-dark transition-colors disabled:opacity-50"
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Export CSV
            </button>
          </div>
        </div>

        {leads === null ? (
          <div className="grid place-items-center py-32 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="grid place-items-center py-24 text-center" data-testid="leads-empty">
            <Inbox className="h-10 w-10 text-royal/30" />
            <p className="mt-4 text-muted-foreground">{leads.length === 0 ? "No leads yet." : "No leads match your search."}</p>
          </div>
        ) : (
          <div className="grid gap-3" data-testid="leads-list">
            {filtered.map((l, i) => (
              <motion.div
                key={l.id}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: Math.min(i * 0.02, 0.3) }}
                data-testid={`lead-row-${i}`}
                className="bg-white rounded-lg border border-black/5 p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-display text-xl text-royal-deep truncate">{l.name}</h3>
                      {l.interest && (
                        <span className="text-[11px] tracking-wide uppercase bg-royal/10 text-royal px-2.5 py-1 rounded-full">{l.interest}</span>
                      )}
                      {l.role && (
                        <span className="text-[11px] tracking-wide uppercase bg-terracotta/10 text-terracotta px-2.5 py-1 rounded-full">{roleLabel[l.role] || l.role}</span>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-muted-foreground">
                      <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1.5 hover:text-royal transition-colors"><Mail className="h-3.5 w-3.5" />{l.email}</a>
                      {l.phone && <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{l.phone}</span>}
                      {l.company && <span className="inline-flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" />{l.company}</span>}
                    </div>
                    {l.message && <p className="mt-3 text-sm text-royal-deep/80 max-w-2xl">{l.message}</p>}
                  </div>
                  <div className="text-xs text-muted-foreground whitespace-nowrap md:text-right">{fmtDate(l.created_at)}</div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(null); // null = checking

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setAuthed(false); return; }
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => setAuthed(true))
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); });
  }, []);

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); };

  if (authed === null) {
    return <div className="min-h-screen grid place-items-center bg-bone"><Loader2 className="h-6 w-6 animate-spin text-royal" /></div>;
  }
  return authed ? <Dashboard onLogout={logout} /> : <Login onSuccess={() => setAuthed(true)} />;
}
